import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { routePath } from "../../../config/routeMgt/routePath";
import { message, Spin, Alert, Modal } from "antd";
import { useGetUserInfo } from "../../../hooks/useGetUserInfo";
import { useGetCurrentSubQuery } from "../../../features/pricing/hooks/useGetRecruiterPackage";
import {
  fetchSmartScreenRule,
  isSmartScreenExhausted,
} from "../utils/smartScreenStatus";
import { useGetChatHistory } from "../../chatHistory/components/hooks/useGetChatHistory";
import ItemDetailsPage from "./item-details-page";
import { Item } from "../../../types";

import dayjs from "dayjs";
import { END_POINT } from "../../../config/environment";
import {
  extractTextFromAttachment,
  extractRawTextFromDocument,
  isCvTextReadable,
  MAX_SMART_SCREEN_FILE_BYTES,
  isPdfFile,
  isWordDocument,
  toPdfFileName,
} from "../../../utils/fileUtils";
import { OCRErrorAlert } from "../../../utils/ocr/OCRErrorAlert";
import { useOCRValidation } from "../../../utils/ocr/useOCRValidation";
import { BatchTable } from "./BatchTable";
import { CVUpload } from "./CVUpload";
import { JobDescriptionUpload } from "./JobDescriptionUpload";
import { ProcessingTipsBanner } from "./ProcessingTipsBanner";
import { useFileProcessing } from "../hooks/useFileProcessing";
import CustomEmpty from "../../../components/CustomEmpty";

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: chatHistoryPage,
    isLoading: isHistoryLoading,
    refetch,
  } = useGetChatHistory({ page: 1, perPage: 100 });
  const historyItems = chatHistoryPage?.items ?? [];

  const { token } = useGetUserInfo();
  const { data: currentSubData, isLoading: subStatusLoading } =
    useGetCurrentSubQuery();
  const smartScreenRule = useMemo(
    () =>
      (
        currentSubData?.rules as
        | { label: string; status?: boolean; message?: string }[]
        | undefined
      )?.find((r) => r.label === "smart_screen"),
    [currentSubData],
  );

  const {
    ocrErrorLogs,
    showOcrErrors,
    clearOcrErrorLogs,
    checkDocumentRequiresOCR,
    addOcrErrorLog,
  } = useOCRValidation();

  const {
    processingItems,
    setProcessingItems,
    processingStatus,
    setProcessingStatus,
    progress,
    setProgress,
    generateChatId,
    startSSEForProcessing,
  } = useFileProcessing(checkDocumentRequiresOCR, addOcrErrorLog);

  const [jobDescription, setJobDescription] = useState<any>(null);
  const [jobDescriptionText, setJobDescriptionText] = useState<string>("");
  const [activeItemId, setActiveItemId] = useState<any | null>(null);
  const [activeJDId, setActiveJDId] = useState<number | null>(null);
  const [jdFile, setJdFile] = useState<File | null>(null);
  const [analysisResults, setAnalysisResults] = useState<{
    [key: string | number]: any[];
  }>({});
  const [isProcessing, setProcessing] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [completedChatId, setCompletedChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [latestItems, setLatestItems] = useState<Item[]>([]);
  const [isFetchingLatest, setIsFetchingLatest] = useState(false);
  const [descriptionInputType, setDescriptionInputType] =
    useState<string>("upload");

  const cvInputRef = useRef<HTMLInputElement | null>(null);
  const jobDescriptionTextRef = useRef<string>("");

  const latestHistoryItems = historyItems
    .filter(
      (item: { id: any }) =>
        !processingItems.some((p: { id: any }) => p.id === item.id),
    )
    .slice(0, 5);

  /** Prefer in-flight local batches so progress/status keys stay matched. */
  const mergeWithProcessingItems = useCallback(
    (serverItems: Item[]): Item[] => {
      const processingKeys = new Set(
        processingItems.flatMap((p) =>
          [p.id, p.chatId].filter((k) => k != null).map(String),
        ),
      );

      const filteredServer = serverItems.filter((s) => {
        const keys = [s.id, s.chatId].filter((k) => k != null).map(String);
        return !keys.some((k) => processingKeys.has(k));
      });

      return [...processingItems, ...filteredServer]
        .sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 6);
    },
    [processingItems],
  );

  const fetchLatestItems = useCallback(async (): Promise<Item[]> => {
    try {
      const response = await fetch(
        `${END_POINT.BASE_URL}/v1/chat-history?page=1&per_page=6`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch latest items");
      }

      const data = await response.json();
      const serverItems = Array.isArray(data.data) ? data.data : [];
      return mergeWithProcessingItems(serverItems);
    } catch (error) {
      console.error("Error fetching latest items:", error);
      return mergeWithProcessingItems(historyItems);
    }
  }, [token, historyItems, mergeWithProcessingItems]);

  useEffect(() => {
    const loadLatestItems = async () => {
      setIsFetchingLatest(true);
      const items = await fetchLatestItems();
      setLatestItems(items);
      setIsFetchingLatest(false);
    };

    loadLatestItems();
  }, [fetchLatestItems]);

  const refreshLatestItems = useCallback(async () => {
    const items = await fetchLatestItems();
    setLatestItems(items);
  }, [fetchLatestItems]);

  // Immediate UI: show local processing batches before/without waiting on refetch
  const recentBatchItems = useMemo(
    () => mergeWithProcessingItems(latestItems),
    [latestItems, mergeWithProcessingItems],
  );

  useEffect(() => {
    const extractJDText = async () => {
      if (jdFile) {
        try {
          const text = await extractTextFromAttachment(jdFile);
          setJobDescriptionText(text);
          jobDescriptionTextRef.current = text;
          message.success("Job description processed successfully");
        } catch (error) {
          console.error("Error extracting JD text:", error);
          message.error("Failed to extract text from job description");
          setJobDescriptionText("");
          jobDescriptionTextRef.current = "";
        }
      } else {
        setJobDescriptionText("");
        jobDescriptionTextRef.current = "";
      }
    };
    extractJDText();
  }, [jdFile]);

  const getBatchPreSignedUrls = async (
    files: Array<{ file: File; profileId: string }>,
    chatId: string,
  ): Promise<{ [key: string]: { uploadUrl: string; fileUrl: string } }> => {
    const response = await fetch(
      `${END_POINT.BASE_URL}/v1/generate-presigned-urls`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          files: files.map(({ file, profileId }) => ({
            fileName: toPdfFileName(file.name),
            fileType: "application/pdf",
            chatId,
            profileId,
          })),
        }),
      },
    );

    if (!response.ok) throw new Error("Failed to get batch upload URLs");
    return response.json();
  };

  const uploadToS3WithProgress = async (
    file: File,
    uploadUrl: string,
  ): Promise<void> => {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": "application/pdf" },
    });
    if (!res.ok) throw new Error(`Upload failed with status: ${res.status}`);
  };

  const convertWordAndUpload = async (
    file: File,
    chatId: string,
    profileId: string,
  ): Promise<{
    fileUrl: string;
    fileName: string;
    originalName: string;
    converted: boolean;
    contentType: string;
  }> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("chatId", chatId);
    formData.append("profileId", profileId);

    const response = await fetch(
      `${END_POINT.BASE_URL}/v1/smartscreen/convert-and-upload`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      },
    );

    if (!response.ok) {
      let message = "Failed to convert Word document to PDF";
      try {
        const err = await response.json();
        message = err.message || err.error || message;
      } catch {
        // ignore parse errors
      }
      throw new Error(message);
    }

    const converted = await response.json();
    const fileUrl = String(converted.fileUrl || "");
    const fileName = String(converted.fileName || toPdfFileName(file.name));
    const contentType = String(converted.contentType || "application/pdf");

    if (!fileUrl || !/\.pdf(\?|#|$)/i.test(fileUrl)) {
      throw new Error(
        `${file.name}: conversion did not produce a PDF URL for Smart Screen.`,
      );
    }

    return {
      ...converted,
      fileUrl,
      fileName: toPdfFileName(fileName),
      contentType,
    };
  };

  const processFilesWithBackend = async (
    files: File[],
    chatId: string,
    itemId: any,
  ) => {
    setProcessingStatus((prev) => ({
      ...prev,
      [itemId]: "Starting processing...",
    }));
    setProgress((prev) => ({ ...prev, [itemId]: 5 }));

    try {
      const liveRule = await fetchSmartScreenRule(token);
      if (isSmartScreenExhausted(liveRule)) {
        message.error(
          liveRule?.message ||
            "You have reached your Smart Screen limit. Please upgrade to continue.",
        );
        setProcessingStatus((prev) => ({
          ...prev,
          [itemId]: "Smart Screen limit reached",
        }));
        setProgress((prev) => ({ ...prev, [itemId]: 0 }));
        setProcessing(false);
        return;
      }

      const filesWithProfiles = files.map((file) => ({
        file,
        profileId: generateChatId(),
      }));

      const unsupported = filesWithProfiles.filter(
        ({ file }) => !isPdfFile(file) && !isWordDocument(file),
      );
      if (unsupported.length > 0) {
        throw new Error(
          `Only PDF and Word CVs are supported: ${unsupported
            .map(({ file }) => file.name)
            .join(", ")}`,
        );
      }

      const pdfFiles = filesWithProfiles.filter(
        ({ file }) => isPdfFile(file) && !isWordDocument(file),
      );
      const wordFiles = filesWithProfiles.filter(({ file }) =>
        isWordDocument(file),
      );

      const preSignedUrls =
        pdfFiles.length > 0
          ? await getBatchPreSignedUrls(pdfFiles, chatId)
          : {};

      if (wordFiles.length > 0) {
        setProcessingStatus((prev) => ({
          ...prev,
          [itemId]: `Converting ${wordFiles.length} Word document(s) to PDF...`,
        }));
      }

      const cvTexts: Array<{
        originalName: string;
        profileId: string;
        s3FileUrl: string;
        isOCR: boolean;
        text?: string;
        mimeType?: string;
      }> = [];

      await Promise.all(
        filesWithProfiles.map(async ({ file, profileId }) => {
          try {
            let originalName = file.name;
            let s3FileUrl = "";
            let mimeType = file.type || "application/pdf";

            if (isWordDocument(file)) {
              const converted = await convertWordAndUpload(
                file,
                chatId,
                profileId,
              );
              originalName = toPdfFileName(
                converted.fileName || file.name,
              );
              s3FileUrl = converted.fileUrl;
              mimeType = "application/pdf";
            } else if (isPdfFile(file)) {
              const filePresigned = preSignedUrls[profileId];
              if (!filePresigned) {
                throw new Error(`No upload URL was generated for ${file.name}`);
              }
              await uploadToS3WithProgress(file, filePresigned.uploadUrl);
              originalName = toPdfFileName(file.name);
              s3FileUrl = filePresigned.fileUrl;
              mimeType = "application/pdf";
            } else {
              throw new Error(`${file.name}: unsupported file type`);
            }

            let needsOcr = false;
            try {
              needsOcr = await checkDocumentRequiresOCR(file);
            } catch {
              needsOcr = true;
            }

            let extractedText = "";
            if (!needsOcr) {
              try {
                extractedText = await extractRawTextFromDocument(file);
              } catch (readError) {
                console.warn(
                  `Unreadable CV, routing to OCR: ${file.name}`,
                  readError,
                );
                needsOcr = true;
              }
              if (!isCvTextReadable(extractedText)) {
                needsOcr = true;
              }
            }

            if (needsOcr) {
              cvTexts.push({
                originalName,
                profileId,
                s3FileUrl,
                isOCR: true,
                mimeType,
              });
              return;
            }

            cvTexts.push({
              originalName,
              profileId,
              s3FileUrl,
              isOCR: false,
              text: extractedText,
            });
          } catch (err) {
            console.error(`Error uploading/converting ${file.name}:`, err);
            throw new Error(
              `${file.name}: ${
                err instanceof Error ? err.message : "Failed to process file"
              }`,
            );
          }
        }),
      );

      if (cvTexts.length === 0) {
        setProcessingStatus((prev) => ({
          ...prev,
          [itemId]: "No valid files to process",
        }));
        setProgress((prev) => ({ ...prev, [itemId]: 0 }));
        message.error("No valid files could be processed.");
        setProcessing(false);
        return;
      }

      setProcessingStatus((prev) => ({
        ...prev,
        [itemId]: "Sending files for analysis...",
      }));
      setProgress((prev) => ({ ...prev, [itemId]: 10 }));

      // ── Step 1: Initialize chat + cache ──────────────────────────────────
      const initResponse = await fetch(
        `${END_POINT.BASE_URL}/v1/initialize-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Chatid: chatId,
            jobDescriptionText: jobDescriptionTextRef.current,
            cvCount: cvTexts.length,
          }),
        },
      );

      if (!initResponse.ok) {
        throw new Error("Failed to initialize chat session");
      }

      const ocrCount = cvTexts.filter((cv) => cv.isOCR).length;
      const plainCount = cvTexts.filter((cv) => !cv.isOCR).length;

      setProcessingStatus((prev) => ({
        ...prev,
        [itemId]: "Queueing Smart Screen…",
      }));
      setProgress((prev) => ({ ...prev, [itemId]: 12 }));

      const processRes = await fetch(`${END_POINT.BASE_URL}/v1/process-smart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          Chatid: chatId,
          jobDescriptionText: jobDescriptionTextRef.current,
          cvTexts,
        }),
      });
      const processJson = (await processRes.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      if (!processRes.ok) {
        const errText =
          processJson.error ||
          processJson.message ||
          "You cannot run Smart Screen right now. Please check your plan or try again later.";
        message.error(errText);
        setProcessingStatus((prev) => ({
          ...prev,
          [itemId]: "Smart Screen could not start",
        }));
        setProgress((prev) => ({ ...prev, [itemId]: 0 }));
        setProcessing(false);
        return;
      }

      setProcessing(true);
      startSSEForProcessing(chatId, itemId, async () => {
        setCompletedChatId(String(chatId));
        await refetch();
        await refreshLatestItems();
        setProcessing(false);
        setShowCongratulations(true);
      });

      setProcessingStatus((prev) => ({
        ...prev,
        [itemId]: `Processing ${plainCount} standard + ${ocrCount} OCR CVs…`,
      }));
      setProgress((prev) => ({ ...prev, [itemId]: 15 }));
    } catch (err) {
      console.error("Error processing files:", err);
      setProcessingStatus((prev) => ({
        ...prev,
        [itemId]: "Processing failed - please try again",
      }));
      setProgress((prev) => ({ ...prev, [itemId]: 0 }));
      setProcessing(false);
      message.error(
        err instanceof Error
          ? err.message
          : "Failed to process files. Please try again.",
      );
    }
  };

  const handleUploadJD = (file: File) => {
    setJdFile(file);
    const newJDId = Date.now();
    setActiveJDId(newJDId);
    setJobDescription({
      uid: "-1",
      name: file.name,
      size: file.size,
      type: file.type,
    });
    setJobDescriptionText("");
    jobDescriptionTextRef.current = "";
  };

  const handleJDTextChange = (text: string) => {
    setJobDescriptionText(text);
    jobDescriptionTextRef.current = text;
    if (text && text.length > 0) {
      setJdFile(null);
      setJobDescription(null);
      setActiveJDId(null);
    }
  };

  const handleDescriptionTypeChange = (type: string) => {
    setDescriptionInputType(type);
    if (type === "upload") {
      setJobDescriptionText("");
      jobDescriptionTextRef.current = "";
    } else if (type === "manual") {
      setJdFile(null);
      setJobDescription(null);
      setActiveJDId(null);
    }
  };

  const handleCVInputChange = async (files: File[]) => {
    if (!files || files.length === 0) return;

    const validFiles = files.filter(
      (f) => f.size > 0 && f.size <= MAX_SMART_SCREEN_FILE_BYTES,
    );
    if (validFiles.length !== files.length) {
      const oversized = files.filter(
        (f) => f.size > MAX_SMART_SCREEN_FILE_BYTES,
      );
      oversized.forEach((file) =>
        addOcrErrorLog(
          file.name,
          "File exceeds 1MB and was not sent for Smart Screen processing.",
        ),
      );
      setError(
        `These files exceed 1MB and were not processed:\n${oversized.map((f) => f.name).join("\n")}`,
      );
    }
    if (validFiles.length === 0) return;

    if (descriptionInputType === "upload" && !activeJDId) {
      message.error("Please upload a Job Description file first.");
      return;
    }
    if (
      descriptionInputType === "manual" &&
      !jobDescriptionTextRef.current.trim()
    ) {
      message.error("Please paste a Job Description text first.");
      return;
    }

    if (!subStatusLoading && isSmartScreenExhausted(smartScreenRule)) {
      Modal.warning({
        title: "Smart Screen limit reached",
        content:
          smartScreenRule?.message ||
          "You have no Smart Screen sessions left on your current plan. Open Billing to upgrade, or check your usage on the pricing page.",
      });
      return;
    }

    let chatId: any;
    let affectedItemId: any | null = null;

    setProcessing(true);

    if (activeItemId) {
      chatId = activeItemId;
      affectedItemId = activeItemId;

      setProcessingItems((prev) =>
        prev.map((item) =>
          item.id === activeItemId
            ? {
              ...item,
              files: [
                ...(item.files ?? []),
                ...validFiles.map((f) => ({ name: f.name, size: f.size })),
              ],
              status: "Processing",
              updated_at: dayjs().toISOString(),
            }
            : item,
        ),
      );
      setActiveItemId(null);
    } else {
      chatId = generateChatId();
      const batchNum = allItems.length + 1;
      const newId = Date.now();
      affectedItemId = newId;

      const newItem: Item = {
        id: newId,
        jdId: activeJDId || Date.now(),
        title: `Batch ${batchNum}`,
        files: validFiles.map((f) => ({ name: f.name, size: f.size })),
        created_at: dayjs().toISOString(),
        status: "Processing",
        chatId,
      };

      setProcessingItems((prev) => [...prev, newItem]);
    }

    if (cvInputRef.current) cvInputRef.current.value = "";

    if (affectedItemId) {
      await processFilesWithBackend(validFiles, chatId, affectedItemId);
    }
  };

  const handleDeleteJD = () => {
    setJobDescription(null);
    setJobDescriptionText("");
    jobDescriptionTextRef.current = "";
    setActiveJDId(null);
    setJdFile(null);
  };

const handleOpenDetailsPage = (itemId: string | number) => {
  const item = allItems.find((i) => i.id?.toString() === itemId?.toString());

  if (!item) {
    console.error("Batch not found:", itemId);
    return;
  }

  navigate(
    `${routePath.cvchat}?view=scan-results&batchId=${encodeURIComponent(
      String(item.id),
    )}`,
    {
      state: {
        selectedItem: item,
      },
    },
  );
};

  const handleAddClick = async (item: any) => {
    if (!cvInputRef.current) return;

    if (item.job_description) {
      setJobDescription({
        uid: `jd-${item.id}`,
        name: `JD-${item.id}.txt`,
        size: item.job_description.length,
        type: "text/plain",
      });
      setJobDescriptionText(item.job_description);
      jobDescriptionTextRef.current = item.job_description;
      setActiveJDId(Date.now());
    }

    setActiveItemId(item.id);
    cvInputRef.current.click();
  };

  const handleDeleteSession = async (item: Item) => {
    try {
      setProcessingItems((prev) => prev.filter((p) => p.id !== item.id));

      const response = await fetch(
        `${END_POINT.BASE_URL}/v1/delete-session/${item.id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.ok) {
        message.success("Batch deleted successfully");
        await refetch();
        await refreshLatestItems();
      } else {
        throw new Error("Failed to delete batch");
      }
    } catch {
      message.error("Failed to delete batch");
    }
  };

  const allItems = [...processingItems, ...historyItems];
  const searchParams = new URLSearchParams(location.search);

  const currentView = searchParams.get("view");
  const batchId = searchParams.get("batchId");

  const selectedBatch =
    location.state?.selectedItem ||
    allItems.find((item) => item.id?.toString() === batchId?.toString());

  const isScanResults = currentView === "scan-results";

  const currentJDBatches = activeJDId
    ? allItems.filter((it) => it.jdId === activeJDId)
    : [];

  const showProcessingTips = isProcessing || processingItems.length > 0;
  const activeProcessingItem = processingItems[processingItems.length - 1];
  const activeProcessingStatus = activeProcessingItem
    ? processingStatus[activeProcessingItem.id]
    : null;
  const activeProcessingProgress = activeProcessingItem
    ? progress[activeProcessingItem.id]
    : undefined;

  if (isHistoryLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" tip="Loading document history..." />
      </div>
    );
  }

  if (isScanResults) {
    if (!selectedBatch) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <Spin size="large" tip="Loading batch results..." />
        </div>
      );
    }

    return (
      <ItemDetailsPage
        item={selectedBatch}
        analysisResults={analysisResults}
        onBack={() => {
          navigate(routePath.cvchat, {
            replace: true,
          });
        }}
      />
    );
  }

  return (
    <div className="flex min-h-screen w-full gap-6 p-6 bg-gray-50 flex-col">
      {error && (
        <Alert
          message="Upload Error"
          description={error}
          type="error"
          showIcon
          closable
          className="mb-4"
          onClose={() => setError(null)}
        />
      )}

      <OCRErrorAlert
        ocrErrorLogs={ocrErrorLogs}
        showOcrErrors={showOcrErrors}
        onClear={clearOcrErrorLogs}
      />

      <div className="flex gap-6">
        <JobDescriptionUpload
          jobDescription={jobDescription}
          onUpload={handleUploadJD}
          onDelete={handleDeleteJD}
          jobDescriptionText={jobDescriptionText}
          onTextChange={handleJDTextChange}
          descriptionInputType={descriptionInputType}
          onDescriptionTypeChange={handleDescriptionTypeChange}
        />

        <div className="w-px bg-gray-200" />

        <CVUpload
          jobDescription={jobDescription}
          jobDescriptionText={jobDescriptionText}
          currentJDBatches={currentJDBatches}
          onCVInputChange={handleCVInputChange}
          isScanning={isProcessing}
          descriptionInputType={descriptionInputType}
        />
      </div>

      <ProcessingTipsBanner
        visible={showProcessingTips}
        statusText={activeProcessingStatus}
        progressPercent={activeProcessingProgress}
      />

      <BatchTable
        title="Just Uploaded"
        items={recentBatchItems}
        processingStatus={processingStatus}
        progress={progress}
        processingItems={processingItems}
        onItemClick={handleOpenDetailsPage}
        onAddClick={handleAddClick}
        onDeleteSession={handleDeleteSession}
      />

      {processingItems.length === 0 && latestHistoryItems.length === 0 && (
        <div className="mt-8">
          <CustomEmpty message="No document batches uploaded yet." />
        </div>
      )}

      <Modal
        open={showCongratulations}
        onCancel={() => setShowCongratulations(false)}
        footer={null}
        centered
        width={480}
      >
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
          <h2 style={{ marginBottom: 8 }}>Your resumes are ready!</h2>
          <p style={{ color: "#666", marginBottom: 24 }}>
            All candidates have been analysed and ranked. Click on any batch to
            view the results.
          </p>
          <button
            onClick={() => {
              setShowCongratulations(false);

              if (!completedChatId) return;

              const completedItem = [...latestItems, ...historyItems].find(
                (candidate) =>
                  String(candidate.id) === completedChatId ||
                  String(candidate.chatId) === completedChatId,
              );

              navigate(
                `${routePath.cvchat}?view=scan-results&batchId=${encodeURIComponent(
                  completedChatId,
                )}`,
                {
                  state: {
                    selectedItem: {
                      ...(completedItem ?? {}),
                      id: completedChatId,
                      chatId: completedChatId,
                      title: completedItem?.title ?? "Completed batch",
                      files: completedItem?.files ?? [],
                      status: "Completed",
                    } as Item,
                  },
                },
              );
            }}
            style={{
              background: "#1677ff",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 32px",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            View Results
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UploadPage;
